import React from 'react';
import { Card, Empty, Badge, Button, Spin } from 'antd';
import {
	GiftOutlined,
	TrophyOutlined,
	CheckCircleOutlined,
	ClockCircleOutlined,
} from '@ant-design/icons';
import { motion, AnimatePresence } from 'motion/react';
import './style.scss';

const RecentActivityCard = ({
	recentActivities = [],
	onClaimGift,
	onViewRecognition,
	onMarkAsSeen,
	disableSpotlight = false,
	isLoading = false,
	isTierMode = false,
	isCombinedMode = false,
	pinnedContent = null,
}) => {
	const handleDirectSnappyLink = (snappyGift) => {
		if (snappyGift?.link) {
			window.open(snappyGift.link, '_blank');
		}
	};
	const getActivityIcon = (type, status) => {
		switch (type) {
			case 'badge_assignment':
				return <TrophyOutlined style={{ color: '#E75DA7' }} />;
			case 'gift_received':
				return <GiftOutlined style={{ color: '#E75DA7' }} />;
			case 'gift_redeemed':
				return <CheckCircleOutlined style={{ color: '#1890ff' }} />;
			case 'gift_expired':
				return <ClockCircleOutlined style={{ color: '#ff4d4f' }} />;
			case 'tier_claim':
				return <TrophyOutlined style={{ color: '#E75DA7' }} />;
			case 'gift_uncreated':
				return <TrophyOutlined style={{ color: '#E75DA7' }} />;
			default:
				return <TrophyOutlined style={{ color: '#E75DA7' }} />;
		}
	};

	const getActivityTitle = (activity) => {
		switch (activity.type) {
			case 'badge_assignment':
				return `New Recognition: ${activity.badgeName}`;
			case 'gift_received':
				return `Reward from "${activity.badgeName || activity.title || 'Recognition'}"`;
			case 'gift_redeemed':
				return `Reward from "${activity.badgeName || activity.title || 'Recognition'}" Redeemed`;
			case 'gift_expired':
				return `Reward from "${activity.badgeName || activity.title || 'Recognition'}" Expired`;
			case 'tier_claim':
				return `Tier "${activity.tierName}" is Unlocked`;
			case 'gift_uncreated':
				return `Reward from "${activity.badgeName || 'Recognition'}"`;
			default:
				return 'Activity Update';
		}
	};

	const getActivityDescription = (activity) => {
		switch (activity.type) {
			case 'badge_assignment':
				return activity.sentWithoutSnappyGrant
					? 'Recognition badge earned'
					: 'Recognition with gift attached';
			case 'gift_received':
				return 'Click to claim your reward from Snappy';
			case 'gift_redeemed':
				return 'You have successfully redeemed your reward';
			case 'gift_expired':
				return 'This reward has expired and is no longer available';
			case 'tier_claim':
				return activity.tierDescription || 'Tier reward is ready to claim';
			case 'gift_uncreated':
				return 'Click to claim your reward from Snappy';
			default:
				return activity.description || '';
		}
	};

	const renderActivityAction = (activity) => {
		let buttonText;

		switch (activity.type) {
			case 'badge_assignment':
				if (
					!activity.sentWithoutSnappyGrant &&
					activity.status === 'unviewed'
				) {
					return (
						<Button
							type="primary"
							size="small"
							onClick={() => onViewRecognition?.(activity)}
						>
							View Recognition
						</Button>
					);
				}
				return null;
			case 'gift_uncreated':
				buttonText = activity?.isProcessing
					? 'Processing...'
					: activity.giftStatus === 'unwrapped' ||
						  activity.giftStatus === 'opened'
						? 'Finish Claiming Reward'
						: 'Claim Reward';

				return (
					<motion.div
						initial={false}
						animate={
							activity.shouldShake && !disableSpotlight
								? {
										x: [-2, 2, -2, 2, 0],
										rotate: [-1, 1, -1, 1, 0],
									}
								: {}
						}
						transition={{
							duration: 0.5,
							repeat: activity.shouldShake && !disableSpotlight ? Infinity : 0,
							repeatType: 'reverse',
							repeatDelay: 3.0,
							ease: 'easeInOut',
							delay:
								activity.shouldShake && !disableSpotlight
									? (activity.shakeIndex || 0) * 0.3
									: 0,
						}}
						style={{ display: 'inline-block' }}
					>
						<Button
							type="link"
							size="large"
							icon={<GiftOutlined />}
							onClick={() => onClaimGift?.(activity)}
							loading={activity.isProcessing}
							disabled={activity.isProcessing}
							style={{
								fontSize: '15px',
								color: '#6294FF',
								pointerEvents: 'auto',
							}}
						>
							<strong style={{ marginLeft: '4px' }}>{buttonText}</strong>
						</Button>
					</motion.div>
				);
			case 'gift_received':
				buttonText =
					activity.giftStatus === 'unwrapped' ||
					activity.giftStatus === 'opened'
						? 'Finish Claiming Reward'
						: 'Claim Reward';

				return (
					<motion.div
						initial={false}
						animate={
							activity.shouldShake && !disableSpotlight
								? {
										x: [-2, 2, -2, 2, 0],
										rotate: [-1, 1, -1, 1, 0],
									}
								: {}
						}
						transition={{
							duration: 0.5,
							repeat: activity.shouldShake && !disableSpotlight ? Infinity : 0,
							repeatType: 'reverse',
							repeatDelay: 3.0, // 3 seconds between shake cycles
							ease: 'easeInOut',
							delay:
								activity.shouldShake && !disableSpotlight
									? (activity.shakeIndex || 0) * 0.3
									: 0,
						}}
						style={{ display: 'inline-block' }} // Ensure proper display
					>
						<Button
							type="link"
							size="large"
							icon={<GiftOutlined />}
							onClick={() => onClaimGift?.(activity)}
							style={{
								fontSize: '15px',
								color: '#6294FF',
								pointerEvents: 'auto', // Ensure button is clickable
							}}
						>
							<strong style={{ marginLeft: '4px' }}>{buttonText}</strong>
						</Button>
					</motion.div>
				);
			case 'tier_claim':
				const hasSnappyLink = activity.hasSnappyLink;
				const hasGiftLink = activity.hasGiftLink;
				const hasAnyLink = activity.hasLink;
				const isProcessing = activity.isProcessing;

				if (hasSnappyLink && activity.existingSnappyGift) {
					buttonText = 'Finish Claiming Reward';
					return (
						<Button
							type="link"
							size="large"
							icon={<TrophyOutlined />}
							onClick={() =>
								handleDirectSnappyLink(activity.existingSnappyGift)
							}
							style={{
								fontSize: '15px',
								color: '#6294FF',
								pointerEvents: 'auto',
							}}
						>
							<strong style={{ marginLeft: '4px' }}>{buttonText}</strong>
						</Button>
					);
				} else {
					buttonText = isProcessing
						? 'Processing...'
						: hasAnyLink
							? 'Finish Claiming Reward'
							: 'Claim Reward';
					return (
						<Button
							type="link"
							size="large"
							icon={<TrophyOutlined />}
							onClick={() => onClaimGift?.(activity)}
							loading={isProcessing}
							disabled={isProcessing}
							style={{
								fontSize: '15px',
								color: '#6294FF',
								pointerEvents: 'auto',
							}}
						>
							<strong style={{ marginLeft: '4px' }}>{buttonText}</strong>
						</Button>
					);
				}
			default:
				return null;
		}
	};

	const formatTimeAgo = (timestamp) => {
		const now = new Date();
		const activityTime = new Date(timestamp);
		const diffInMinutes = Math.floor((now - activityTime) / (1000 * 60));

		if (diffInMinutes < 1) return 'Just now';
		if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

		const diffInHours = Math.floor(diffInMinutes / 60);
		if (diffInHours < 24) return `${diffInHours}h ago`;

		const diffInDays = Math.floor(diffInHours / 24);
		if (diffInDays < 7) return `${diffInDays}d ago`;

		return activityTime.toLocaleDateString();
	};

	const claimActivities = isCombinedMode
		? recentActivities
		: isTierMode
			? recentActivities.filter((activity) => activity.type === 'tier_claim')
			: recentActivities.filter(
					(activity) => activity.type === 'gift_received'
				);
	return (
		<div className="recent-activity-card">
			{claimActivities.filter((a) => !a.hasSeen).length > 0 && (
				<div className="recent-activity-header">
					<p></p>
					<Badge count={claimActivities.filter((a) => !a.hasSeen).length} />
				</div>
			)}

			<div className="recent-activity-content">
				{pinnedContent}
				{isLoading ? (
					<div
						style={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							minHeight: '200px',
						}}
					>
						<Spin />
					</div>
				) : claimActivities.length === 0 && pinnedContent ? null : claimActivities.length === 0 ? (
					<div
						style={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							minHeight: '200px',
						}}
					>
						<Empty
							description="All Caught Up!"
							image={Empty.PRESENTED_IMAGE_SIMPLE}
						/>
					</div>
				) : (
					<div className="activity-list">
						{claimActivities.map((activity, index) => (
							<Card
								key={activity.id || index}
								className={`activity-item ${activity.isNew ? 'new-activity' : ''} ${activity.hasSeen ? 'seen-activity' : ''}`}
								size="small"
								onClick={() => {
									if (!activity.hasSeen && onMarkAsSeen) {
										onMarkAsSeen(activity);
									}
								}}
								style={{ cursor: activity.hasSeen ? 'default' : 'pointer' }}
							>
								<div className="activity-content">
									<div className="activity-icon">
										{getActivityIcon(activity.type, activity.status)}
									</div>
									<div className="activity-details">
										<div className="activity-title">
											{getActivityTitle(activity)}
											{activity.isNew && !activity.hasSeen && (
												<Badge dot className="new-indicator" />
											)}
										</div>
										<div className="activity-description">
											{getActivityDescription(activity)}
										</div>
										<div className="activity-time">
											{formatTimeAgo(activity.createdAt)}
										</div>
									</div>
									<div className="activity-action">
										{renderActivityAction(activity)}
									</div>
								</div>
							</Card>
						))}
					</div>
				)}
			</div>
		</div>
	);
};

export default RecentActivityCard;
